import React, { Fragment, useContext, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import AppContext from "../../context/AppContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FiLogOut, FiX } from "react-icons/fi";
import translations from "./Translations";
import { clearAuthSession } from "../../utils/authSession";

export default function Logout({ showModal, setShowModal }) {
  const cancelButtonRef = useRef(null);
  const {
    langMode,
    setWorkflowId,
    setSelectedOption,
    setRcOrgName,
    setRcOrgId,
    setRcWorkflowId,
    setRcWorkflowName,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const isRtl = langMode === "ar";

  const handleConfirmLogout = () => {
    setShowModal(false);
    clearAuthSession();
    setRcOrgName("");
    setRcOrgId("");
    setSelectedOption("");
    setWorkflowId("");
    setRcWorkflowId("");
    setRcWorkflowName("");
    navigate("/");
    toast.success(translations[langMode].logout);
  };

  const handleCancelLogout = () => {
    setShowModal(false);
  };

  return (
    <Transition.Root show={showModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[200]"
        initialFocus={cancelButtonRef}
        onClose={() => setShowModal(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/55 backdrop-blur-[2px] transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-3 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-3 sm:scale-95"
            >
              <Dialog.Panel
                data-testid="logout-confirmation-modal"
                dir={isRtl ? "rtl" : "ltr"}
                className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-6 text-left shadow-xl"
              >
                <button
                  type="button"
                  className="absolute end-4 top-4 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-50 hover:text-slate-600"
                  onClick={handleCancelLogout}
                  ref={cancelButtonRef}
                  aria-label={translations[langMode].cancel || "Cancel"}
                >
                  <FiX className="h-5 w-5" />
                </button>

                <div className="flex flex-col items-center text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EEF4FF] text-[#2B7FFF]">
                    <FiLogOut className="h-6 w-6" strokeWidth={2} />
                  </span>

                  <Dialog.Title className="mt-4 text-lg font-bold tracking-tight text-[#111827]">
                    {translations[langMode].Logout?.trim() || "Logout"}
                  </Dialog.Title>

                  <p className="mt-2 text-sm leading-6 text-[#6b7280]">
                    {translations[langMode].loogoutMsg}
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handleCancelLogout}
                    data-testid="logout-cancel"
                    title={translations[langMode].cancelandstay}
                    className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    {translations[langMode].cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmLogout}
                    data-testid="logout-confirm"
                    title={translations[langMode].confirmandlogout}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#2B7FFF] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1d6ef2]"
                  >
                    <FiLogOut className="h-4 w-4" aria-hidden="true" />
                    {translations[langMode].Logout?.trim() || "Logout"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
